// skipcq: JS-W1028, JS-0128
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import ScrapedBeerTable from './ScrapedBeerTable';

const FIELD_SEPARATOR = '\u001f';
const ROW_SEPARATOR = '\u001e';

export function scrapedBeerTableIdentity(selectedUrl, beers) {
  const beerIdentity = (Array.isArray(beers) ? beers : [])
    .map((beer) => [
      beer?.beer_name || '',
      beer?.beer_style || '',
      beer?.beer_abv || '',
      beer?.beer_description || '',
    ].join(FIELD_SEPARATOR))
    .join(ROW_SEPARATOR);
  return `${selectedUrl || ''}:${beerIdentity}`;
}

/**
 * ScrapedBeerResults
 * Displays the results of a scraped beer page, including company information,
 * import status messages, and a table of beers.
 */
function ScrapedBeerResults({
  companyName,
  companyDescription,
  companyAddress,
  beers,
  selectedUrl,
  importError,
  styleMapping,
  styleTypes,
  styleTypeByName,
  onAssignStyleType,
  onSelectionChange,
}) {
  return (
    <>
      <Box sx={{
        mb: 3
      }}>
        {companyName && (
          <Typography variant="h6" gutterBottom>
            {companyName}
          </Typography>
        )}
        {companyDescription && (
          <Typography variant="body2" paragraph sx={{
            color: "text.secondary"
          }}>
            {companyDescription}
          </Typography>
        )}
        {companyAddress && (
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            {companyAddress}
          </Typography>
        )}
        {selectedUrl && (
          <Box sx={{
            mt: 2
          }}>
            <Typography variant="body2" component="span" sx={{
              color: "text.secondary"
            }}>
              Source:{' '}
            </Typography>
            <Link
              href={selectedUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ wordBreak: 'break-word' }}
            >
              {selectedUrl}
            </Link>
          </Box>
        )}
      </Box>
      {importError && (
        <Box
          sx={{
            mt: 2,
            mb: 2
          }}>
          <Typography variant="body2" color="error">
            {importError}
          </Typography>
        </Box>
      )}
      <ScrapedBeerTable
        key={scrapedBeerTableIdentity(selectedUrl, beers)}
        beers={beers}
        styleMapping={styleMapping}
        styleTypes={styleTypes}
        styleTypeByName={styleTypeByName}
        onAssignStyleType={onAssignStyleType}
        onSelectionChange={onSelectionChange}
      />
    </>
  );
}

ScrapedBeerResults.propTypes = {
  companyName: PropTypes.string,
  companyDescription: PropTypes.string,
  companyAddress: PropTypes.string,
  beers: PropTypes.arrayOf(
    PropTypes.shape({
      beer_name: PropTypes.string,
      beer_style: PropTypes.string,
      beer_abv: PropTypes.string,
    }),
  ).isRequired,
  selectedUrl: PropTypes.string,
  importError: PropTypes.string,
  styleMapping: PropTypes.object,
  styleTypes: PropTypes.arrayOf(PropTypes.object),
  styleTypeByName: PropTypes.object,
  onAssignStyleType: PropTypes.func,
  onSelectionChange: PropTypes.func,
};

ScrapedBeerResults.defaultProps = {
  companyName: '',
  companyDescription: '',
  companyAddress: '',
  selectedUrl: null,
  importError: null,
  styleMapping: {},
  styleTypes: [],
  styleTypeByName: {},
  onAssignStyleType: null,
  onSelectionChange: null,
};

export default ScrapedBeerResults;
